import { defineStore } from 'pinia'
import {
  PermissionSet,
  AuthorizationRelationResponse
} from '@/Network/Types/Responses/AuthorizationResponses'
import {
  Authorization,
  AuthorizationPermissionCheck,
  AuthorizationResourcesAndSubjects
} from '@/types/authorization'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import Network from '@/Network'
import { SubjectCandidateResponse } from '@/Network/Types/Responses/AuthorizationResponses'
import { getApiErrorMessage, getApiErrorStatus } from '@/utils/apiError'

export const PERMISSIONS_LOAD_ERROR = 'Não foi possível carregar suas permissões.'

interface PermissionsState {
  permissions: PermissionSet | null
  isLoading: boolean
  /**
   * Falha na PRIMEIRA carga do set (rede, timeout, 5xx). Com ela, `permissions`
   * fica `null` e `check()` nega tudo; o guard da rota leva à tela de retry.
   */
  loadError: string | null
  sharedPermissions: AuthorizationRelationResponse[]
  isLoadingShared: boolean
  resourceCandidates: SubjectCandidateResponse[]
  isLoadingResourceCandidates: boolean
  candidatePagination: {
    limit: number
    offset: number
    hasMore: boolean
  }
  sharedPermissionsPagination: {
    limit: number
    offset: number
    hasMore: boolean
    total: number
  }
}

// Cada resource type que tem endpoint dedicado de listagem mapeia para o método
// correspondente do Network. Os níveis company ignoram companyId; os níveis
// health_unit/schedule usam companyId!. Resource types ausentes caem no
// listPermissions genérico (ver _fetchPermissionsFromApi).
type PermissionListFetcher = (
  resourceId: number,
  companyId: number | undefined,
  search: string | undefined,
  limit: number,
  offset: number
) => ReturnType<typeof Network.permission.listPermissions>

const PERMISSION_LIST_FETCHERS: Partial<
  Record<AuthorizationResourcesAndSubjects, PermissionListFetcher>
> = {
  [AuthorizationResourcesAndSubjects.DsAttendance]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) =>
    Network.permission.listDsAttendancePermissions(resourceId, companyId!, search, limit, offset),
  [AuthorizationResourcesAndSubjects.DsAttendanceCompany]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) => Network.permission.listDsAttendanceCompanyPermissions(resourceId, search, limit, offset),
  [AuthorizationResourcesAndSubjects.DsAttendanceHealthUnit]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) =>
    Network.permission.listDsAttendanceHealthUnitPermissions(
      resourceId,
      companyId!,
      search,
      limit,
      offset
    ),
  [AuthorizationResourcesAndSubjects.NpsCompany]: (resourceId, companyId, search, limit, offset) =>
    Network.permission.listNpsCompanyPermissions(resourceId, search, limit, offset),
  [AuthorizationResourcesAndSubjects.NpsHealthUnit]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) =>
    Network.permission.listNpsHealthUnitPermissions(resourceId, companyId!, search, limit, offset),
  [AuthorizationResourcesAndSubjects.PostStationCompany]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) => Network.permission.listPostStationCompanyPermissions(resourceId, search, limit, offset),
  [AuthorizationResourcesAndSubjects.PostStationHealthUnit]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) =>
    Network.permission.listPostStationHealthUnitPermissions(
      resourceId,
      companyId!,
      search,
      limit,
      offset
    ),
  [AuthorizationResourcesAndSubjects.ProfessionalDocumentsCompany]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) =>
    Network.permission.listProfessionalDocumentsCompanyPermissions(
      resourceId,
      search,
      limit,
      offset
    ),
  [AuthorizationResourcesAndSubjects.StationExceptionalitiesCompany]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) => Network.permission.listStationExceptionalitiesByCompany(resourceId, search, limit, offset),
  [AuthorizationResourcesAndSubjects.StationExceptionalitiesHealthUnit]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) =>
    Network.permission.listStationExceptionalitiesByHealthUnit(
      resourceId,
      companyId!,
      search,
      limit,
      offset
    ),
  [AuthorizationResourcesAndSubjects.StationExceptionalitiesSchedule]: (
    resourceId,
    companyId,
    search,
    limit,
    offset
  ) =>
    Network.permission.listStationExceptionalitiesBySchedule(
      resourceId,
      companyId!,
      search,
      limit,
      offset
    )
}

async function _fetchPermissionsFromApi(
  resourceType: AuthorizationResourcesAndSubjects,
  resourceId: number,
  companyId: number | undefined,
  search: string | undefined,
  limit: number,
  offset: number
) {
  const fetcher = PERMISSION_LIST_FETCHERS[resourceType]
  if (fetcher) return fetcher(resourceId, companyId, search, limit, offset)
  return Network.permission.listPermissions(
    resourceType,
    resourceId,
    companyId,
    search,
    limit,
    offset
  )
}

/**
 * Somam ~60s. Sem fila no outbox o backend leva ate ~10s de ciclo do permission-worker,
 * mais 10s se as relacoes da escala cairem em dois lotes, 1s de poll do refresher e
 * 7 a 15s de recompute: o pior caso normal passa de 30s.
 */
const PERMISSIONS_REFRESH_BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 30000]

/** Serializa refetches concorrentes: so a resposta da geracao mais nova e aplicada. */
let permissionsFetchGeneration = 0

/** A carga da geracao mais nova (ver `waitForLatestLoad`). */
let latestPermissionsLoad: Promise<void> = Promise.resolve()

/**
 * Uma chamada mais nova assumiu enquanto esta voava: esperar por ela antes de
 * devolver. Sem isto, quem aguarda a geracao descartada (o guard da rota) leria
 * o estado intermediario — `permissions` null e `loadError` null — e liberaria
 * uma rota sem `authorizations` com as permissoes ainda nao carregadas.
 */
async function waitForLatestLoad(own: Promise<void>): Promise<void> {
  let settled = own
  await settled
  while (latestPermissionsLoad !== settled) {
    settled = latestPermissionsLoad
    await settled
  }
}

export const usePermissionsStore = defineStore('permissions', {
  state: (): PermissionsState => ({
    permissions: null,
    isLoading: false,
    loadError: null,
    sharedPermissions: [],
    isLoadingShared: false,
    resourceCandidates: [],
    isLoadingResourceCandidates: false,
    candidatePagination: {
      limit: 20,
      offset: 0,
      hasMore: true
    },
    sharedPermissionsPagination: {
      limit: 20,
      offset: 0,
      hasMore: true,
      total: 0
    }
  }),

  actions: {
    async fetchPermissions(force = false, companyId?: number | string) {
      const { flags } = useFeatureFlags()

      if (!flags.enabledPermissions) {
        this.permissions = {}
        return
      }

      if (!force && this.permissions) return

      // Refresh em background: ja existe um set valido em memoria. Nao sinalizamos
      // isLoading porque `check()` devolve false enquanto ele estiver ligado
      // (:379) — o conteudo ja renderizado sumiria da tela durante o round-trip.
      const isBackgroundRefresh = Boolean(this.permissions)
      if (!isBackgroundRefresh) {
        this.isLoading = true
        // Um retry nao exibe o erro da tentativa anterior enquanto voa. Uma falha
        // velha que chegue depois continua descartada pela guarda de geracao.
        this.loadError = null
      }

      // Guarda de geracao: dois refetches concorrentes (duas mutacoes seguidas)
      // podiam terminar fora de ordem e a resposta mais ANTIGA sobrescrever a
      // mais nova — perdendo de vez o recurso recem-criado, ja que os dois loops
      // param ao encontrar a propria chave.
      permissionsFetchGeneration += 1
      const generation = permissionsFetchGeneration
      const isLatest = () => generation === permissionsFetchGeneration

      const load = (async () => {
        try {
          const data = await Network.authorization.getMyPermissions(companyId, force)
          if (!isLatest()) return
          this.permissions = data || {}
          this.loadError = null
        } catch (error) {
          console.error('Failed to fetch permissions:', error)
          // Num refresh, manter o set anterior. Zerar aqui transformaria um blip de
          // rede em aplicacao inteira bloqueada (check() e fail-closed por chave
          // ausente), sem caminho de volta a nao ser recarregar a pagina. E uma
          // falha velha nao pode apagar o set que uma chamada mais nova ja trouxe.
          if (isBackgroundRefresh || !isLatest()) return

          // Primeira carga: `null` ("nao carregado"), nunca `{}`. O `{}` era memoizado
          // como set valido e nenhuma navegacao tentava de novo. `null` mantem check()
          // negando tudo e deixa a proxima chamada refazer o fetch.
          this.permissions = null
          // 401: o interceptor ja limpou a sessao e recarrega em `/` — nao ha retry.
          if (getApiErrorStatus(error) === 401) return
          this.loadError = getApiErrorMessage(error, PERMISSIONS_LOAD_ERROR)
        } finally {
          // So a geracao mais nova desliga o loading: a velha terminaria antes dela.
          if (!isBackgroundRefresh && isLatest()) this.isLoading = false
        }
      })()
      latestPermissionsLoad = load

      await waitForLatestLoad(load)
    },

    /**
     * Refetch forcado apos uma mutacao que muda o que o usuario pode LER
     * (criar/editar/remover escala). `fetchPermissions` memoiza o set pela sessao
     * inteira do SPA, entao sem isto a escala nova so aparece apos F5.
     *
     * `fetchPermissions` nao rejeita -- ele loga e, num refresh, preserva o set
     * anterior. Isso e intencional: a mutacao ja foi persistida e nao pode virar
     * erro falso de criacao na tela.
     */
    async refreshAfterMutation(waitFor?: {
      resourceKey: string
      permission: string
    }): Promise<boolean> {
      await this.fetchPermissions(true)

      if (!waitFor) return true

      // Mesmo criterio do `check()`: a chave pode existir por outra permissao e
      // ainda assim a tela nao renderizar o recurso.
      const isVisible = () => this.permissions?.[waitFor.resourceKey]?.[waitFor.permission] === true

      if (isVisible()) return true

      // O backend nao publica a permissao de forma sincrona: a criacao grava em
      // `permission_outbox` e um worker leva as relationships ao SpiceDB a cada
      // ~10s; so entao o refresher recomputa o cache Redis. Um refetch unico
      // disparado logo apos o POST chega cedo demais e recebe o set antigo.
      //
      // Backoff somando ~60s, parando assim que o recurso aparece. Se estourar,
      // devolvemos false em vez de insistir: a tela segue utilizavel e o proximo
      // login/F5 corrige.
      for (const delayMs of PERMISSIONS_REFRESH_BACKOFF_MS) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
        await this.fetchPermissions(true)

        if (isVisible()) return true
      }

      console.warn(
        `Permissão ${waitFor.permission} em ${waitFor.resourceKey} não apareceu no tempo esperado; a tela pode precisar de um recarregamento.`
      )
      return false
    },

    async fetchSharedPermissions(
      resourceType: AuthorizationResourcesAndSubjects,
      resourceId: number,
      companyId?: number,
      search?: string,
      isLoadMore = false
    ) {
      if (!isLoadMore) {
        this.sharedPermissionsPagination.offset = 0
        this.sharedPermissionsPagination.hasMore = true
        this.sharedPermissions = []
      }

      if (!this.sharedPermissionsPagination.hasMore && isLoadMore) {
        return []
      }

      this.isLoadingShared = true
      try {
        const limit = this.sharedPermissionsPagination.limit
        const offset = this.sharedPermissionsPagination.offset

        const response = await _fetchPermissionsFromApi(
          resourceType,
          resourceId,
          companyId,
          search,
          limit,
          offset
        )

        const permissionsArray = response.data || []
        const totalCount = response.count || 0

        this.sharedPermissionsPagination.total = totalCount

        if (isLoadMore) {
          this.sharedPermissions = [...this.sharedPermissions, ...permissionsArray]
        } else {
          this.sharedPermissions = permissionsArray
        }

        this.sharedPermissionsPagination.offset += this.sharedPermissionsPagination.limit
        this.sharedPermissionsPagination.hasMore =
          this.sharedPermissions.length < this.sharedPermissionsPagination.total

        return permissionsArray
      } catch (error) {
        console.error('Failed to fetch shared permissions:', error)
        throw error
      } finally {
        this.isLoadingShared = false
      }
    },

    resetSharedPagination() {
      this.sharedPermissionsPagination = {
        limit: 20,
        offset: 0,
        hasMore: true,
        total: 0
      }
      this.sharedPermissions = []
    },

    bulkCheck(authorizations: AuthorizationPermissionCheck[]) {
      const { flags } = useFeatureFlags()

      if (!flags.enabledPermissions) {
        return true
      }

      if (!authorizations || authorizations.length === 0) {
        return true
      }

      return authorizations.some((authItem) => this.check(authItem))
    },

    clearPermissions() {
      // Invalida a carga em voo da sessao anterior: sem isto ela repovoaria
      // `permissions`/`loadError` depois do logout (e o `finally` dela nao desliga
      // mais o loading, por isso desligamos aqui).
      permissionsFetchGeneration += 1
      this.isLoading = false
      this.permissions = null
      this.loadError = null
      this.sharedPermissions = []
      this.resetSharedPagination()
    },

    async fetchResourceCandidates(
      resourceType: AuthorizationResourcesAndSubjects,
      resourceId: number,
      companyId: number,
      search?: string,
      isLoadMore = false
    ) {
      if (this.isLoadingResourceCandidates) return

      if (!isLoadMore) {
        this.candidatePagination.offset = 0
        this.candidatePagination.hasMore = true
        this.resourceCandidates = []
      }

      if (!this.candidatePagination.hasMore) return

      this.isLoadingResourceCandidates = true
      try {
        const data = await Network.permission.listResourceCandidates(
          resourceType,
          resourceId,
          companyId,
          search,
          this.candidatePagination.limit,
          this.candidatePagination.offset
        )

        if (data.length < 1) {
          this.candidatePagination.hasMore = false
        }

        if (isLoadMore) {
          this.resourceCandidates.push(...data)
        } else {
          this.resourceCandidates = data
        }

        this.candidatePagination.offset += this.candidatePagination.limit
      } catch (error) {
        console.error('Failed to fetch resource candidates:', error)
        if (!isLoadMore) this.resourceCandidates = []
      } finally {
        this.isLoadingResourceCandidates = false
      }
    },

    clearResourceCandidates() {
      this.resourceCandidates = []
      this.candidatePagination = {
        limit: 20,
        offset: 0,
        hasMore: true
      }
    },

    async checkPermissionImpact(payload: {
      resource_id: number
      resource_type: AuthorizationResourcesAndSubjects
      subjects: { subject_id: number; subject_type: string }[]
    }) {
      try {
        return await Network.permission.checkPermissionImpact(payload)
      } catch (error) {
        console.error('Failed to check permission impact:', error)
        return []
      }
    }
  },

  getters: {
    check: (state) => {
      const checkSinglePermission = (auth: AuthorizationPermissionCheck): boolean => {
        const { flags } = useFeatureFlags()

        if (!flags.enabledPermissions) {
          return true
        }

        if (state.isLoading || !state.permissions) {
          return false
        }

        const resourceKey = auth.resource.objectId
          ? `${auth.resource.objectType}:${auth.resource.objectId}`
          : auth.resource.objectType

        const resourcePerms = state.permissions[resourceKey]

        if (!resourcePerms) return false

        return resourcePerms[auth.permission] === true
      }

      return (
        authorization: AuthorizationPermissionCheck | AuthorizationPermissionCheck[]
      ): boolean => {
        if (Array.isArray(authorization)) {
          return authorization.every((authItem) => checkSinglePermission(authItem))
        }

        return checkSinglePermission(authorization)
      }
    },

    checkAnyRessources: (state) => {
      return (authorization: Authorization): boolean => {
        const { flags } = useFeatureFlags()

        if (!flags.enabledPermissions) {
          return true
        }

        if (!state.permissions || state.isLoading) {
          return false
        }

        const { resourceType, permission } = authorization

        return Object.entries(state.permissions).some(([key, perms]) => {
          const [currentResourceType] = key.split(':')
          if (currentResourceType === resourceType) {
            return perms[permission] === true
          }
          return false
        })
      }
    }
  }
})
